import React from "react";
import LegalLayout, { H2, P, UL, LI } from "../components/LegalLayout";
import { CookieIllustration } from "../components/legal/LegalIllustrations";

const SECTIONS = [
  { id: "essential", labelFr: "Stockage essentiel", labelEn: "Essential storage" },
  { id: "analytics", labelFr: "Analyse et publicité", labelEn: "Analytics and advertising" },
  { id: "choices", labelFr: "Tes choix", labelEn: "Your choices" },
];

// Page liée depuis CookieConsentBanner.tsx — honnête sur l'état réel : OLTODO n'utilise
// aujourd'hui que du stockage essentiel (voir utils/api.ts, aucun `document.cookie`, seulement
// localStorage), jamais de cookie publicitaire ou analytique. Si un futur script d'analyse est
// ajouté, il devra passer par utils/cookieConsent.ts#hasAnalyticsConsent() avant de se charger.
export default function CookiePolicy() {
  return (
    <LegalLayout
      titleFr="Politique de cookies" titleEn="Cookie Policy"
      taglineFr="Ce qu'on stocke dans ton navigateur, et pourquoi."
      taglineEn="What we store in your browser, and why."
      updatedAt="7 septembre 2026"
      illustration={<CookieIllustration />}
      sections={SECTIONS}
      tldrFr={[
        "OLTODO n'utilise aucun cookie publicitaire ou de mesure d'audience aujourd'hui.",
        "Seul du stockage essentiel (session, langue) est utilisé, il ne nécessite pas de consentement.",
        "Effacer ce stockage depuis ton navigateur te déconnecte simplement d'OLTODO, rien de plus.",
      ]}
      tldrEn={[
        "OLTODO uses no advertising or audience-measurement cookies today.",
        "Only essential storage (session, language) is used, it doesn't require consent.",
        "Clearing this storage from your browser simply signs you out of OLTODO, nothing more.",
      ]}
    >
      {(lang) => lang === "fr" ? (
        <>
          <P>
            Cette politique explique comment OLTODO utilise les cookies et technologies de stockage similaires
            (comme le stockage local de ton navigateur) sur le site oltodo.com.
          </P>

          <H2 id="essential">1. Stockage essentiel</H2>
          <P>
            OLTODO utilise uniquement du stockage local essentiel au fonctionnement du site, garder ta session
            ouverte, retenir la langue choisie, et sécuriser ton compte. Ce stockage ne peut pas être désactivé
            sans casser le fonctionnement du site, il ne nécessite pas de consentement au sens du RGPD, mais nous
            t'en informons par transparence.
          </P>
          <UL>
            <LI>Session et jeton de connexion, pour rester connecté d'une visite à l'autre.</LI>
            <LI>Langue préférée, pour afficher l'application dans la langue que tu as choisie.</LI>
          </UL>

          <H2 id="analytics">2. Cookies d'analyse ou publicitaires</H2>
          <P>
            OLTODO n'utilise aujourd'hui aucun cookie d'analyse, de mesure d'audience ou de publicité ciblée, ni
            Google Analytics, ni pixel Facebook, ni équivalent. Si cela venait à changer, nous te demanderions
            ton consentement au préalable, via le même bandeau que celui qui t'a mené à cette page, et tu
            pourrais le retirer à tout moment.
          </P>

          <H2 id="choices">3. Tes choix</H2>
          <P>
            Le stockage local peut être effacé à tout moment depuis les réglages de ton navigateur. Cela te
            déconnectera d'OLTODO. Pour toute question, contacte-nous à contact@oltodo.com.
          </P>
        </>
      ) : (
        <>
          <P>
            This policy explains how OLTODO uses cookies and similar storage technologies (such as your
            browser's local storage) on oltodo.com.
          </P>

          <H2 id="essential">1. Essential Storage</H2>
          <P>
            OLTODO only uses local storage that's essential to the site working, keeping you signed in,
            remembering your chosen language, and keeping your account secure. This storage can't be disabled
            without breaking the site, it doesn't require consent under GDPR, but we disclose it for
            transparency.
          </P>
          <UL>
            <LI>Session and login token, to keep you signed in between visits.</LI>
            <LI>Preferred language, to show the app in the language you chose.</LI>
          </UL>

          <H2 id="analytics">2. Analytics or Advertising Cookies</H2>
          <P>
            OLTODO does not currently use any analytics, audience-measurement, or targeted advertising cookies,
            no Google Analytics, no Facebook pixel, nor any equivalent. If this were to change, we would ask for
            your consent beforehand, through the same banner that led you to this page, and you could withdraw
            it at any time.
          </P>

          <H2 id="choices">3. Your Choices</H2>
          <P>
            Local storage can be cleared at any time from your browser settings. Doing so will sign you out of
            OLTODO. For any question, contact us at contact@oltodo.com.
          </P>
        </>
      )}
    </LegalLayout>
  );
}
